import { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import SeasonBanner from "@/components/SeasonBanner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Home from "@/pages/Home";
import Commander from "@/pages/Commander";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

// Pages secondaires chargées à la demande (réduit le JavaScript initial)
const ZonePage = lazy(() => import("@/pages/ZonePage"));
const Devis = lazy(() => import("@/pages/Devis"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));
const Admin = lazy(() => import("@/pages/Admin"));
const Evenements = lazy(() => import("@/pages/Evenements"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const MetierPage = lazy(() => import("@/pages/MetierPage"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));

const BUSINESS_LD = {
  "@context": "https://schema.org",
  "@type": "FoodEstablishment",
  name: "Gourmandise Traiteur",
  description:
    "Brunch fait maison livré à domicile à Lyon et dans la Drôme. Formules dès 23,90 €/personne. 100% halal, sans porc.",
  url: "https://brunchadomicile.fr",
  telephone: "+33769276021",
  email: "devis@gourmandise-traiteur.fr",
  priceRange: "€€",
  servesCuisine: "Brunch",
  aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "6" },
  areaServed: [
    "Lyon", "Villeurbanne", "Bron", "Caluire-et-Cuire", "Saint-Priest", "Décines-Charpieu",
    "Valence", "Romans-sur-Isère", "Bourg-de-Péage", "Crest", "Loriol-sur-Drôme", "Montélimar",
  ],
  location: [
    { "@type": "Place", address: { "@type": "PostalAddress", streetAddress: "PARC du Chêne", addressLocality: "Bron", postalCode: "69500", addressCountry: "FR" } },
    { "@type": "Place", address: { "@type": "PostalAddress", streetAddress: "105 rue des Mourettes", addressLocality: "Valence", postalCode: "26000", addressCountry: "FR" } },
  ],
};

const zoneLd = (name, address, area) => ({
  "@context": "https://schema.org",
  "@type": "FoodEstablishment",
  name: `Gourmandise Traiteur — ${name}`,
  description: `Livraison de brunch à domicile : ${area.join(", ")}.`,
  url: "https://brunchadomicile.fr",
  telephone: "+33769276021",
  email: "devis@gourmandise-traiteur.fr",
  priceRange: "€€",
  servesCuisine: "Brunch",
  areaServed: area,
  address,
});

const LYON_LD = zoneLd(
  "Brunch à domicile Lyon",
  { "@type": "PostalAddress", streetAddress: "PARC du Chêne", addressLocality: "Bron", postalCode: "69500", addressCountry: "FR" },
  ["Lyon", "Villeurbanne", "Bron", "Caluire-et-Cuire", "Vaulx-en-Velin", "Décines-Charpieu", "Saint-Priest", "Chassieu", "Vénissieux", "Écully", "Tassin-la-Demi-Lune", "Oullins", "Meyzieu", "Vienne", "Trévoux", "Meximieux", "Châtillon-sur-Chalaronne", "Ambérieu-en-Bugey", "Pont-d'Ain", "Mâcon", "Bourg-en-Bresse", "Tournus", "Chalon-sur-Saône"]
);

const DROME_LD = zoneLd(
  "Brunch à domicile Valence et Drôme",
  { "@type": "PostalAddress", streetAddress: "105 rue des Mourettes", addressLocality: "Valence", postalCode: "26000", addressCountry: "FR" },
  ["Valence", "Guilherand-Granges", "Bourg-lès-Valence", "Saint-Péray", "Portes-lès-Valence", "Alixan", "Chabeuil", "Livron-sur-Drôme", "Tain-l'Hermitage", "Romans-sur-Isère", "Bourg-de-Péage", "Tournon-sur-Rhône", "Loriol-sur-Drôme", "Crest", "Montélimar", "Beaurepaire", "Saint-Marcellin"]
);

const SEO = {
  home: {
    title: "Brunch à Domicile à Lyon & dans la Drôme — Livraison Traiteur | Gourmandise Traiteur",
    description:
      "Brunch fait maison livré à domicile à Lyon, Bron, Villeurbanne, Valence, Romans-sur-Isère et toute la Drôme. Formules dès 23,90 €/pers., réservation de créneau et paiement en ligne sécurisé. 100% halal, sans porc.",
    path: "/",
    jsonLd: BUSINESS_LD,
  },
  lyon: {
    title: "Brunch à Domicile Lyon, Villeurbanne & Bron — Livraison dès 8h | Gourmandise Traiteur",
    description:
      "Traiteur brunch à Lyon et toute la métropole, et aussi dans l'Ain (Trévoux, Bourg-en-Bresse), la Saône-et-Loire (Mâcon, Chalon) et l'Isère (Vienne). Livraison premium depuis notre atelier de Bron, offerte jusqu'à 10 km. Commandez la veille, payez en ligne.",
    path: "/lyon",
    jsonLd: LYON_LD,
  },
  drome: {
    title: "Brunch à Domicile Valence, Romans-sur-Isère & Drôme — Livraison | Gourmandise Traiteur",
    description:
      "Traiteur brunch à Valence et dans la Drôme : Romans-sur-Isère, Bourg-de-Péage, Crest, Loriol, Tain-l'Hermitage, Montélimar… et jusqu'à Beaurepaire et Saint-Marcellin en Isère. Livraison premium depuis notre atelier de Valence, offerte jusqu'à 10 km. Réservation et paiement en ligne.",
    path: "/drome",
    jsonLd: DROME_LD,
  },
  commander: {
    title: "Commander un Brunch à Domicile — Lyon, Valence & Drôme | Gourmandise Traiteur",
    description:
      "Commandez votre brunch à domicile en quelques minutes : formule, options, créneau de livraison, adresse. Livraison offerte jusqu'à 10 km. Commandez la veille pour le lendemain à Lyon ou dans la Drôme.",
    path: "/commander",
  },
  devis: {
    title: "Devis Brunch Sur Mesure — EVJF, Mariage, Entreprise | Gourmandise Traiteur",
    description:
      "Grazing table, brunch d'EVJF, lendemain de mariage, événement d'entreprise à Lyon ou dans la Drôme : décrivez votre projet et recevez une proposition sur mesure sous 24 h ouvrées.",
    path: "/devis",
  },
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-cream text-espresso">
        <SeasonBanner />
        <Navbar />
        <main>
          <Suspense fallback={<div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-sand border-t-terracotta" /></div>}>
          <Routes>
            <Route path="/" element={<><Seo {...SEO.home} /><Home /></>} />
            <Route path="/lyon" element={<><Seo {...SEO.lyon} /><ZonePage zone="lyon" /></>} />
            <Route path="/drome" element={<><Seo {...SEO.drome} /><ZonePage zone="drome" /></>} />
            <Route path="/commander" element={<><Seo {...SEO.commander} /><Commander /></>} />
            <Route path="/evenements" element={<><Seo title="Brunch EVJF, Mariage & Entreprise — Traiteur Événementiel Lyon & Drôme | Gourmandise Traiteur" description="Grazing table, brunch d'EVJF, lendemain de mariage, brunch d'entreprise à Lyon et dans la Drôme : tables dressées sur place, devis sous 24 h. 100% halal, sans porc." path="/evenements" /><Evenements /></>} />
            <Route path="/blog" element={<><Seo title="Idées Brunch & Recettes — Le Journal | Gourmandise Traiteur" description="Conseils de traiteur, recettes maison (pancakes moelleux, œufs brouillés crémeux) et guides brunch : EVJF, mariage, brunch à domicile à Lyon et dans la Drôme." path="/blog" /><Blog /></>} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/brunch-a-domicile/:slug" element={<CityPage />} />
            <Route path="/brunch-airbnb" element={<MetierPage page="brunch-airbnb" />} />
            <Route path="/brunch-entreprise" element={<MetierPage page="brunch-entreprise" />} />
            <Route path="/brunch-lendemain-mariage" element={<MetierPage page="brunch-lendemain-mariage" />} />
            <Route path="/livraison-petit-dejeuner" element={<MetierPage page="livraison-petit-dejeuner" />} />
            <Route path="/plateaux-repas-lyon" element={<MetierPage page="plateaux-repas-lyon" />} />
            <Route path="/plateaux-repas-valence" element={<MetierPage page="plateaux-repas-valence" />} />
            <Route path="/brunch-halloween" element={<MetierPage page="brunch-halloween" />} />
            <Route path="/brunch-noel" element={<MetierPage page="brunch-noel" />} />
            <Route path="/devis" element={<><Seo {...SEO.devis} /><Devis /></>} />
            <Route path="/mentions-legales" element={<><Seo title="Mentions Légales | Gourmandise Traiteur" description="Mentions légales de Gourmandise Traiteur — SIREN 752 128 645, Bron (69) et Valence (26)." path="/mentions-legales" noindex /><MentionsLegales /></>} />
            <Route path="/admin" element={<><Seo title="Espace pro | Gourmandise Traiteur" description="Espace réservé." path="/admin" noindex /><Admin /></>} />
            <Route path="/paiement/succes" element={<><Seo title="Paiement confirmé | Gourmandise Traiteur" description="Confirmation de commande." path="/paiement/succes" noindex /><PaymentSuccess /></>} />
            <Route path="/paiement/annule" element={<><Seo title="Paiement interrompu | Gourmandise Traiteur" description="Paiement interrompu." path="/paiement/annule" noindex /><PaymentCancel /></>} />
            <Route path="*" element={<><Seo {...SEO.home} /><Home /></>} />
          </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
      <WhatsAppFloat />
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
